import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID, Service } from '@angular/core';
import { environment } from '@env/environment';
import { isPrimaryUrl } from './app-routes.enum';
import { resolveV1LegacyHandoff, V1LegacyHandoff } from './v1-legacy-deep-link';
import { StorageService } from '@core/storage/storage.service';
import { StorageKeys } from '@core/storage/storage.keys';
import { LANG_QUERY_PARAM, parseLangParam, readLangQueryParam, v1AcceptsLangParam } from '@core/language/lang-param';
import { DEFAULT_LANGUAGE as APP_DEFAULT_LANGUAGE, Language as AppLanguage } from '@core/i18n/domain/language';

/**
 * v1's localStorage key for its own current-language string (see
 * `shared/config/constants/storage.keys.ts`'s `StorageKeys.LANGUAGE`). Inlined
 * as a literal rather than importing that legacy file, so this v2 file has zero
 * imports crossing outside `src/app/v2` — MUST stay byte-identical to that
 * value if it is ever touched (same duplication shape, and same reasoning, as
 * `TALBINAH_TOKEN` in `v2-http.service.ts`; see
 * docs/v2-isolation/ESCALATIONS.md E2).
 */
const V1_LANGUAGE_STORAGE_KEY = 'PatientTalbinahPortalLanguage';

/**
 * LegacyHandoffService — the pure v2-relevant computation that used to live
 * as private methods on the root `App` (`app.ts`, v1's
 * `app-root`, which bootstraps BOTH v1 and v2 and decides which one paints).
 *
 * Extracted per docs/v2-isolation/ESCALATIONS.md E4 / DECISIONS.md D9 so this
 * logic is v2-owned, independently reusable, and ready for a future v2-only
 * bootstrap once v1 is deleted — WITHOUT changing `App`'s own
 * orchestration or observable behavior today. Every method here is a verbatim
 * port: same inputs, same outputs, same side effects, just relocated.
 * `App` still owns the `currentUrl`/`isPrimaryRoute`/`legacyHandoffPending`
 * signals (its template binds to them), the one-shot `legacyHandoffStarted`
 * guard, and the actual `router.navigate` call in its `runLegacyHandoff` —
 * those orchestrate App's OWN state, not pure v2 computation, so they
 * were deliberately left in place rather than moved here.
 *
 * One disclosed simplification: the original methods logged via legacy's
 * static `Logger` (`common/core/utilities/logging`) at several points — pure
 * `console.debug`/`console.warn` diagnostics with no functional effect. Not
 * reproduced here (avoids importing legacy `Logger`, or adopting v2's own
 * `LoggerService` mid-port, for lines with zero observable behavior).
 */
@Service()
export class LegacyHandoffService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly storage = inject(StorageService);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  /**
   * Resolve the legacy-path handoff + urgent-care flag for the ENTRY url.
   * Verbatim port of `App`'s constructor lines that computed
   * `legacyHandoff`/`urgentHandoff` — preserving, in particular, that
   * `urgentHandoff` is NOT itself gated by `isBrowser` (only
   * `legacyHandoff` is, via the ternary below); that asymmetry is
   * intentional in the original and is kept exactly.
   */
  resolveEntryHandoff(
    pathname: string | null | undefined,
    search: string | null | undefined,
  ): { legacyHandoff: V1LegacyHandoff | null; urgentHandoff: boolean } {
    const legacyHandoff = this.isBrowser ? resolveV1LegacyHandoff(pathname, search) : null;
    const urgentHandoff = !legacyHandoff && this.hasUrgentCareParams(search ?? '');
    return { legacyHandoff, urgentHandoff };
  }

  /**
   * Does `url` belong to the redesign (i.e. is it NOT a legacy `/v1` url)?
   * Env-gated: with `tbStandalone === false` the redesign renders inside v1
   * chrome, where v1 keeps owning `?lang=`.
   */
  isPrimaryVisit(url: string | null | undefined): boolean {
    return environment.tbStandalone !== false && isPrimaryUrl(url);
  }

  /** `?isEmergency` / `?isMedicine` present-and-truthy on a raw query string. */
  protected hasUrgentCareParams(search: string): boolean {
    if (!search) return false;
    const params = new URLSearchParams(search);
    return (
      this.isUrgentCareFlag(params.get('isEmergency')) || this.isUrgentCareFlag(params.get('isMedicine'))
    );
  }

  /** A truthy urgent-care deep-link flag (`true` · `'true'` · `'1'`). */
  isUrgentCareFlag(value: unknown): boolean {
    return value === true || value === 'true' || value === '1';
  }

  /**
   * Apply `?lang=ar|en` carried by the ENTRY url. Runs from `App`'s
   * constructor — the last moment before the lazy /v2 subtree boots and reads
   * the stored language, so the redesigned surface paints in the requested
   * language on its FIRST frame (no flip, no reload).
   *
   * Ownership, by url shape:
   *   · a `/v2/...` url, or a legacy-path/urgent-care deep link about to be
   *     handed off there → v2 owns the param: apply it to both language
   *     systems and STRIP it.
   *   · any other v1 url → only SEED the v2 store (so /v2 agrees whenever it
   *     is next opened) and leave the param in place: v1's own handler
   *     switches v1 and needs the page reload it performs to do so
   *     consistently.
   * A junk value or no param at all changes nothing.
   */
  applyEntryLangParam(legacyHandoff: V1LegacyHandoff | null, urgentHandoff: boolean): void {
    if (!this.isBrowser) return;

    const search = this.document.location?.search;
    const requested = readLangQueryParam(search);
    if (!requested) return;

    const ownedByPrimary =
      this.isPrimaryVisit(this.document.location?.pathname) || !!legacyHandoff || urgentHandoff;

    if (!ownedByPrimary) {
      if (v1AcceptsLangParam(search)) this.seedLanguage(requested);
      return;
    }

    this.applyLanguageChange(requested);
    this.stripLangParamFromLocation();
  }

  /**
   * The v2 language change, applied from the v1 root — the persisted half of
   * what `LanguageFacade.setLanguage` does (v2's `LanguageStore`/
   * `TranslationFacade` are route-scoped to /v2 and unreachable from here, but
   * they both resolve FROM this key, so persisting it is the whole mechanism).
   * v1's key is written too (raw) so v1 agrees on the language the link asked
   * for; `changeLanguage` itself is NOT called — it ends in a page reload,
   * which on a fresh entry would throw away the bootstrap being seeded.
   * Returns whether the language actually changed.
   */
  applyLanguageChange(lang: AppLanguage): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;

    const changed = this.seedLanguage(lang);
    try {
      localStorage.setItem(V1_LANGUAGE_STORAGE_KEY, lang);
    } catch {
      // best-effort — matches the original's swallow-and-log (log dropped, see class doc).
    }
    this.setHtmlAttrs(lang);
    return changed;
  }

  /**
   * Persist the v2 language exactly as `LanguageService.save` does (same key,
   * same JSON encoding via `StorageService`), and report whether it differs
   * from what was stored. The comparison authority is the PERSISTED value
   * (falling back to v2's default), never a live signal — v2's `LanguageStore`
   * has not run `LanguageFacade.init()` yet on the path that matters here.
   */
  private seedLanguage(lang: AppLanguage): boolean {
    const stored = parseLangParam(this.storage.get<string>(StorageKeys.LANGUAGE));
    const changed = (stored ?? APP_DEFAULT_LANGUAGE) !== lang;
    this.storage.set(StorageKeys.LANGUAGE, lang);
    return changed;
  }

  /**
   * Drop a consumed `?lang=` from the address bar with `history.replaceState`
   * — NOT a router navigation (see the original `App` doc for why:
   * avoids a race with Care's own one-shot query-param clearing, and keeps
   * Back from re-firing the param).
   */
  stripLangParamFromLocation(): void {
    const win = this.document.defaultView;
    if (!win?.history || !win.location) return;

    try {
      const url = new URL(win.location.href);
      if (!url.searchParams.has(LANG_QUERY_PARAM)) return;
      url.searchParams.delete(LANG_QUERY_PARAM);
      win.history.replaceState(win.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    } catch {
      // best-effort — matches the original's swallow-and-log.
    }
  }

  /** Sets `<html lang/dir/class>` for `lang` (mirrors `App.setHtmlAttrs`). */
  private setHtmlAttrs(lang: AppLanguage): void {
    if (!this.isBrowser || !this.document?.documentElement) return;
    const html = this.document.documentElement;
    const dir = lang === AppLanguage.Ar ? 'rtl' : 'ltr';
    html.setAttribute('lang', lang);
    html.setAttribute('dir', dir);
    html.className = lang === AppLanguage.Ar ? AppLanguage.Ar : AppLanguage.En;
  }
}
